import React, { useCallback, useEffect, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CInputGroup,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBell, cilCheckCircle, cilPlus, cilReload, cilSearch, cilWarning } from '@coreui/icons'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import AutoNotifications from './AutoNotifications'
import GameNotificationService from 'src/service/GameNotificationService'
import './GameNotification.css'

const TOGGLE_TYPES = ['fixture', 'trading', 'lockout']

const GameNotificationOld = () => {
  const [users, setUsers] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [titleFilter, setTitleFilter] = useState('')
  const [addVisible, setAddVisible] = useState(false)
  const [editNotificationId, setEditNotificationId] = useState(null)

  const [autoSettings, setAutoSettings] = useState({
    fixture: false,
    trading: false,
    lockout: false,
  })
  const [autoSettingsLoading, setAutoSettingsLoading] = useState(true)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    if (titleFilter) params.append('title', titleFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    try {
      const result = await GameNotificationService.getNotification(
        offset,
        itemsPerPage,
        activePage,
        params,
      )
      setUsers(result.data)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnSorter, titleFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setAutoSettingsLoading(true)
    GameNotificationService.getAutoNotificationSetting()
      .then((res) => {
        if (res.status === 200) {
          setAutoSettings({
            fixture: !!res.data?.fixture_notification,
            trading: !!res.data?.trading_notification,
            lockout: !!res.data?.lockout_notification,
          })
        }
      })
      .finally(() => setAutoSettingsLoading(false))
  }, [])

  // Debounce the search box, same as the other refreshed listings.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if (titleFilter === term) return
      setActivePage(1)
      setTitleFilter(term)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, titleFilter])

  const autoOnCount = TOGGLE_TYPES.filter((type) => autoSettings[type]).length

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Notifications</h1>
          <p className="ge-page-subtitle">
            Send in-app announcements to members and control the alerts the app sends on its own.
          </p>
        </div>
        <CButton color="primary" onClick={() => setAddVisible(true)}>
          <CIcon icon={cilPlus} className="me-1" />
          Add Notification
        </CButton>
      </div>

      {error && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load your notifications.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="gn-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="gn-stat-icon gn-stat-icon--primary">
                <CIcon icon={cilBell} size="lg" />
              </span>
              <div>
                <div className="gn-stat-label">Total Notifications</div>
                <div className="gn-stat-value">{users?.total ?? 0}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="gn-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="gn-stat-icon gn-stat-icon--info">
                <CIcon icon={cilCheckCircle} size="lg" />
              </span>
              <div>
                <div className="gn-stat-label">Auto Alerts On</div>
                <div className="gn-stat-value">
                  {autoSettingsLoading ? '—' : `${autoOnCount}/${TOGGLE_TYPES.length}`}
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="gn-panel mb-3">
        <CCardBody>
          <div className="gn-panel-head">
            <h2 className="gn-panel-title">Automatic Alerts</h2>
            <p className="gn-panel-note">
              These send on their own, straight to the app - no notification needs to be written.
            </p>
          </div>
          <AutoNotifications settings={autoSettings} setSettings={setAutoSettings} />
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title..."
                aria-label="Search notifications by title"
              />
            </CInputGroup>
          </div>

          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnSorter={columnSorter}
            loading={loading}
            onEditNotification={setEditNotificationId}
          />
        </CCardBody>
      </CCard>

      <AddForm visible={addVisible} onClose={() => setAddVisible(false)} fetchUsers={fetchUsers} />

      <EditForm
        notificationId={editNotificationId}
        visible={!!editNotificationId}
        onClose={() => setEditNotificationId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default GameNotificationOld
